import React, { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/solid'
import { cx } from 'class-variance-authority'
import { useAudioPlayer } from '../AudioProvider'
import { PlayButton } from './PlayButton'
import { MuteButton } from './MuteButton'
import { RewindButton } from './RewindButton'
import { ForwardButton } from './ForwardButton'
import { AudioSlider } from './AudioSlider'
import { PlaybackRateButton } from './PlaybackRateButton'
import { formatHumanTime } from '../../../../Utils/Date'
import { useUIStore } from '../../../../stores/ui/uiStore'

export const AudioPlayer = () => {
  const player = useAudioPlayer()

  const wasPlayingRef = useRef(false)

  const [currentTime, setCurrentTime] = useState<number | null>(
    player.currentTime,
  )
  const { isAudioPlayerSmall, setIsAudioPlayerSmall } = useUIStore()

  useEffect(() => {
    setCurrentTime(null)
  }, [player.currentTime])

  if (!player.episode) {
    return null
  }

  return (
    <div
      className={cx(
        'relative flex items-center gap-6 px-4 py-4 shadow bg-white/90 shadow-slate-200/80 ring-1 ring-slate-900/5 backdrop-blur-sm md:px-6 transform',
        isAudioPlayerSmall
          ? 'pr-0 flex-col items-start pb-24 max-w-md'
          : 'md:pr-24',
      )}
    >
      <div className="hidden md:block">
        <img
          src={player.episode.itunes_image.href}
          alt={`Pochette de l'épisode ${player.episode.title}`}
          className="w-16 h-16 rounded-lg"
        />
      </div>
      <div className="hidden md:block">
        <PlayButton player={player} />
      </div>
      {!isAudioPlayerSmall && (
        <div className="mb-[env(safe-area-inset-bottom)] flex flex-1 flex-col gap-3 overflow-hidden p-1">
          <a
            href={player.episode.link}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-bold leading-6 text-center truncate md:text-left"
            title={player.episode.title}
          >
            {player.episode.title}
          </a>
          <div className="flex justify-between gap-6">
            <div className="flex items-center md:hidden">
              <MuteButton player={player} />
            </div>
            <div className="flex items-center flex-none gap-4">
              <RewindButton player={player} />
              <div className="md:hidden">
                <PlayButton player={player} />
              </div>
              <ForwardButton player={player} />
            </div>
            <AudioSlider
              label="Current time"
              maxValue={player.duration}
              step={1}
              value={[currentTime ?? player.currentTime]}
              onChange={([value]: [number]) => setCurrentTime(value)}
              onChangeEnd={([value]: [number]) => {
                player.seek(value)
                if (wasPlayingRef.current) {
                  player.play()
                }
              }}
              numberFormatter={{ format: formatHumanTime } as Intl.NumberFormat}
              onChangeStart={() => {
                wasPlayingRef.current = player.playing
                player.pause()
              }}
            />
            <div className="flex items-center gap-4">
              <div className="flex items-center">
                <PlaybackRateButton player={player} />
              </div>
              <div className="items-center hidden md:flex">
                <MuteButton player={player} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="absolute top-0 right-0 p-2 text-night">
        <button
          type="button"
          className=""
          onClick={() => setIsAudioPlayerSmall()}
        >
          <ChevronDownIcon
            className={cx(
              'w-6 h-6 text-night transform ease-in-out transition-all duration-300',
              isAudioPlayerSmall && 'rotate-180',
            )}
          />
        </button>
        <button
          type="button"
          onClick={() => {
            player.removeEpisode()
          }}
        >
          <XMarkIcon className="w-6 h-6 text-night" />
        </button>
      </div>
    </div>
  )
}
